import { reconciliationMessages } from "@/services/messages";
import { create } from "zustand";

export type TabType =
	| "upload"
	| "rates"
	| "verification"
	| "synonyms"
	| "file"
	| "activity-log"
	| "help"
	| "users"
	| "ai-settings"
	| "reconciliation-list"
	| "reconciliation-create"
	| "reconciliation-detail"
	| "reconciliation-result";

export interface AppTab {
	id: string;
	type: TabType;
	title: string;
	closable: boolean;
	fileId?: number;
	setId?: number;
	/** Идентификатор прогона для табов `reconciliation-result` (фича 023, FR-026). */
	runId?: string;
	/** Флаг устаревания отчёта после re-run (FR-021, R4). */
	stale?: boolean;
}

interface TabState {
	tabs: AppTab[];
	activeTabId: string;
	setActiveTab: (tabId: string) => void;
	openUploadTab: () => void;
	openRatesTab: () => void;
	openVerificationTab: () => void;
	openSynonymsTab: () => void;
	openActivityLogTab: () => void;
	openHelpTab: () => void;
	openUsersTab: () => void;
	openAiSettingsTab: () => void;
	openFileTab: (fileId: number, fileName: string) => void;
	openReconciliationListTab: () => void;
	openReconciliationCreateTab: (setId?: number) => void;
	openReconciliationDetailTab: (setId: number, name: string) => void;
	openReconciliationResultTab: (setId: number, runId: string, setName: string) => void;
	markRunResultStale: (runId: string) => void;
	unmarkRunResultStale: (runId: string) => void;
	closeReconciliationResultTab: (runId: string) => void;
	closeTab: (tabId: string) => void;
	/**
	 * Закрывает все closable-табы (detail + result + create-edit), привязанные
	 * к указанному `setId`. Используется при удалении набора, чтобы не оставлять
	 * stale-табы с кэшированными результатами уже несуществующего набора.
	 */
	closeTabsForSet: (setId: number) => void;
}

const initialTabs: AppTab[] = [{ id: "upload", type: "upload", title: "Файлы", closable: false }];

function trimTitle(title: string, max = 32): string {
	if (title.length <= max) return title;
	return `${title.slice(0, max - 1)}…`;
}

export const useTabStore = create<TabState>((set) => ({
	tabs: initialTabs,
	activeTabId: "upload",

	setActiveTab: (tabId) =>
		set((state) => {
			if (!state.tabs.some((tab) => tab.id === tabId)) {
				return {};
			}
			return { activeTabId: tabId };
		}),

	openUploadTab: () => {
		set((state) => {
			const existing = state.tabs.find((tab) => tab.type === "upload");
			if (existing) {
				return { activeTabId: existing.id };
			}
			const tab: AppTab = { id: "upload", type: "upload", title: "Файлы", closable: false };
			return { tabs: [...state.tabs, tab], activeTabId: tab.id };
		});
	},

	openRatesTab: () => {
		set((state) => {
			const existing = state.tabs.find((tab) => tab.type === "rates");
			if (existing) {
				return { activeTabId: existing.id };
			}
			const tab: AppTab = {
				id: "rates",
				type: "rates",
				title: "Книга расценок",
				closable: false,
			};
			return { tabs: [...state.tabs, tab], activeTabId: tab.id };
		});
	},

	openVerificationTab: () => {
		set((state) => {
			const existing = state.tabs.find((tab) => tab.type === "verification");
			if (existing) {
				return { activeTabId: existing.id };
			}
			const tab: AppTab = {
				id: "verification",
				type: "verification",
				title: "Проверка",
				closable: false,
			};
			return { tabs: [...state.tabs, tab], activeTabId: tab.id };
		});
	},

	openSynonymsTab: () => {
		set((state) => {
			const existing = state.tabs.find((tab) => tab.type === "synonyms");
			if (existing) {
				return { activeTabId: existing.id };
			}
			const tab: AppTab = {
				id: "synonyms",
				type: "synonyms",
				title: "Словарь синонимов",
				closable: false,
			};
			return { tabs: [...state.tabs, tab], activeTabId: tab.id };
		});
	},

	openActivityLogTab: () => {
		set((state) => {
			const existing = state.tabs.find((tab) => tab.type === "activity-log");
			if (existing) {
				return { activeTabId: existing.id };
			}
			const tab: AppTab = {
				id: "activity-log",
				type: "activity-log",
				title: "Логи",
				closable: false,
			};
			return { tabs: [...state.tabs, tab], activeTabId: tab.id };
		});
	},

	openHelpTab: () => {
		set((state) => {
			const existing = state.tabs.find((tab) => tab.type === "help");
			if (existing) {
				return { activeTabId: existing.id };
			}
			const tab: AppTab = {
				id: "help",
				type: "help",
				title: "Справка",
				closable: false,
			};
			return { tabs: [...state.tabs, tab], activeTabId: tab.id };
		});
	},

	openUsersTab: () => {
		set((state) => {
			const existing = state.tabs.find((tab) => tab.type === "users");
			if (existing) {
				return { activeTabId: existing.id };
			}
			const tab: AppTab = {
				id: "users",
				type: "users",
				title: "Пользователи",
				closable: false,
			};
			return { tabs: [...state.tabs, tab], activeTabId: tab.id };
		});
	},

	openAiSettingsTab: () => {
		set((state) => {
			const existing = state.tabs.find((tab) => tab.type === "ai-settings");
			if (existing) {
				return { activeTabId: existing.id };
			}
			const tab: AppTab = {
				id: "ai-settings",
				type: "ai-settings",
				title: "AI-индикатор",
				closable: true,
			};
			return { tabs: [...state.tabs, tab], activeTabId: tab.id };
		});
	},

	openFileTab: (fileId, fileName) => {
		set((state) => {
			const id = `file-${fileId}`;
			const existing = state.tabs.find((tab) => tab.id === id);
			if (existing) {
				return { activeTabId: existing.id };
			}
			const tab: AppTab = {
				id,
				type: "file",
				title: trimTitle(fileName),
				closable: true,
				fileId,
			};
			return { tabs: [...state.tabs, tab], activeTabId: tab.id };
		});
	},

	openReconciliationListTab: () => {
		set((state) => {
			const existing = state.tabs.find((tab) => tab.type === "reconciliation-list");
			if (existing) {
				return { activeTabId: existing.id };
			}
			const tab: AppTab = {
				id: "reconciliation-list",
				type: "reconciliation-list",
				title: reconciliationMessages.tab_title_list,
				closable: false,
			};
			return { tabs: [...state.tabs, tab], activeTabId: tab.id };
		});
	},

	openReconciliationCreateTab: (setId) => {
		set((state) => {
			const title =
				setId !== undefined
					? reconciliationMessages.tab_title_edit
					: reconciliationMessages.tab_title_create;
			const existing = state.tabs.find((tab) => tab.type === "reconciliation-create");
			if (existing) {
				// Перезаписываем setId/title в существующем singleton-табе,
				// чтобы переключение «новый ↔ редактирование» работало корректно.
				const nextTabs = state.tabs.map((tab) =>
					tab.id === existing.id ? { ...tab, setId, title } : tab,
				);
				return { tabs: nextTabs, activeTabId: existing.id };
			}
			const tab: AppTab = {
				id: "reconciliation-create",
				type: "reconciliation-create",
				title,
				closable: true,
				setId,
			};
			return { tabs: [...state.tabs, tab], activeTabId: tab.id };
		});
	},

	openReconciliationDetailTab: (setId, name) => {
		set((state) => {
			const id = `reconciliation-detail-${setId}`;
			const existing = state.tabs.find((tab) => tab.id === id);
			if (existing) {
				return { activeTabId: existing.id };
			}
			const tab: AppTab = {
				id,
				type: "reconciliation-detail",
				title: trimTitle(name),
				closable: true,
				setId,
			};
			return { tabs: [...state.tabs, tab], activeTabId: tab.id };
		});
	},

	openReconciliationResultTab: (setId, runId, setName) => {
		set((state) => {
			const id = `reconciliation-result-${runId}`;
			const existing = state.tabs.find((tab) => tab.id === id);
			if (existing) {
				// W4 review fix: при повторном открытии того же runId сбрасываем
				// stale-флаг, если он был установлен. Сценарий: пользователь
				// кликнул «Открыть актуальный» в StaleOverlay, но actualRunId
				// совпал с текущим (race-condition refetch'а runs-списка).
				if (existing.stale) {
					return {
						tabs: state.tabs.map((tab) => (tab.id === id ? { ...tab, stale: false } : tab)),
						activeTabId: existing.id,
					};
				}
				return { activeTabId: existing.id };
			}
			const tab: AppTab = {
				id,
				type: "reconciliation-result",
				title: trimTitle(`${reconciliationMessages.tab_title_result}: ${setName}`),
				closable: true,
				setId,
				runId,
				stale: false,
			};
			return { tabs: [...state.tabs, tab], activeTabId: tab.id };
		});
	},

	markRunResultStale: (runId) => {
		set((state) => {
			const id = `reconciliation-result-${runId}`;
			const target = state.tabs.find((tab) => tab.id === id);
			if (!target || target.stale) return {};
			const nextTabs = state.tabs.map((tab) => (tab.id === id ? { ...tab, stale: true } : tab));
			return { tabs: nextTabs };
		});
	},

	// Sprint 6 / C5.5 — симметричная пара к markRunResultStale. Используется
	// когда run завершился (например, после re-run из стартового таба) и
	// нужно убрать stale-флаг.
	unmarkRunResultStale: (runId) => {
		set((state) => {
			const id = `reconciliation-result-${runId}`;
			const target = state.tabs.find((tab) => tab.id === id);
			if (!target || !target.stale) return {};
			const nextTabs = state.tabs.map((tab) =>
				tab.id === id ? { ...tab, stale: false } : tab,
			);
			return { tabs: nextTabs };
		});
	},

	closeReconciliationResultTab: (runId) => {
		set((state) => {
			const id = `reconciliation-result-${runId}`;
			const targetIndex = state.tabs.findIndex((tab) => tab.id === id);
			if (targetIndex === -1) return {};

			const nextTabs = state.tabs.filter((tab) => tab.id !== id);
			if (nextTabs.length === 0) {
				const fallback = initialTabs[0];
				return { tabs: [fallback], activeTabId: fallback.id };
			}

			let nextActiveTabId = state.activeTabId;
			if (state.activeTabId === id) {
				const samePosition = nextTabs[targetIndex];
				const previous = nextTabs[targetIndex - 1];
				nextActiveTabId = (samePosition ?? previous ?? nextTabs[0]).id;
			}
			return { tabs: nextTabs, activeTabId: nextActiveTabId };
		});
	},

	closeTab: (tabId) => {
		set((state) => {
			const targetIndex = state.tabs.findIndex((tab) => tab.id === tabId);
			if (targetIndex === -1) return {};

			const targetTab = state.tabs[targetIndex];
			if (!targetTab.closable) return {};

			const nextTabs = state.tabs.filter((tab) => tab.id !== tabId);
			if (nextTabs.length === 0) {
				const fallback = initialTabs[0];
				return { tabs: [fallback], activeTabId: fallback.id };
			}

			let nextActiveTabId = state.activeTabId;
			if (state.activeTabId === tabId) {
				const samePosition = nextTabs[targetIndex];
				const previous = nextTabs[targetIndex - 1];
				nextActiveTabId = (samePosition ?? previous ?? nextTabs[0]).id;
			}

			return { tabs: nextTabs, activeTabId: nextActiveTabId };
		});
	},

	closeTabsForSet: (setId) => {
		set((state) => {
			const idsToClose = new Set(
				state.tabs.filter((tab) => tab.closable && tab.setId === setId).map((tab) => tab.id),
			);
			if (idsToClose.size === 0) return {};

			const nextTabs = state.tabs.filter((tab) => !idsToClose.has(tab.id));
			if (nextTabs.length === 0) {
				const fallback = initialTabs[0];
				return { tabs: [fallback], activeTabId: fallback.id };
			}

			let nextActiveTabId = state.activeTabId;
			if (idsToClose.has(state.activeTabId)) {
				// После удаления набора логично выкинуть пользователя в список наборов;
				// если его нет среди оставшихся — в любой первый таб.
				const reconList = nextTabs.find((tab) => tab.type === "reconciliation-list");
				nextActiveTabId = (reconList ?? nextTabs[0]).id;
			}

			return { tabs: nextTabs, activeTabId: nextActiveTabId };
		});
	},
}));

export function useActiveTab(): AppTab | null {
	return useTabStore((state) => state.tabs.find((tab) => tab.id === state.activeTabId) ?? null);
}

export function useTabActions() {
	return {
		setActiveTab: useTabStore((state) => state.setActiveTab),
		openUploadTab: useTabStore((state) => state.openUploadTab),
		openRatesTab: useTabStore((state) => state.openRatesTab),
		openVerificationTab: useTabStore((state) => state.openVerificationTab),
		openSynonymsTab: useTabStore((state) => state.openSynonymsTab),
		openActivityLogTab: useTabStore((state) => state.openActivityLogTab),
		openHelpTab: useTabStore((state) => state.openHelpTab),
		openUsersTab: useTabStore((state) => state.openUsersTab),
		openAiSettingsTab: useTabStore((state) => state.openAiSettingsTab),
		openFileTab: useTabStore((state) => state.openFileTab),
		openReconciliationListTab: useTabStore((state) => state.openReconciliationListTab),
		openReconciliationCreateTab: useTabStore((state) => state.openReconciliationCreateTab),
		openReconciliationDetailTab: useTabStore((state) => state.openReconciliationDetailTab),
		openReconciliationResultTab: useTabStore((state) => state.openReconciliationResultTab),
		markRunResultStale: useTabStore((state) => state.markRunResultStale),
		unmarkRunResultStale: useTabStore((state) => state.unmarkRunResultStale),
		closeReconciliationResultTab: useTabStore((state) => state.closeReconciliationResultTab),
		closeTab: useTabStore((state) => state.closeTab),
		closeTabsForSet: useTabStore((state) => state.closeTabsForSet),
	};
}
